// Tracks the reader's position in the Mo transcript — the Hotwired port of shadcn's
// useMessageScrollerVisibility. Mounted on the scroll viewport; on scroll / DOM change
// it publishes `massimo-visibility:change` on `window` with:
//   - turns:             [{ id, label }] for each anchored turn (a [data-massimo-anchor] row)
//   - currentAnchorId:   the turn the reader is currently on (scroll-spy by detection line)
//   - messageIds:        ids of all tracked message rows, in DOM order
//   - visibleMessageIds: ids of message rows currently intersecting the viewport
// Recomputes live, so appended turns/messages flow to consumers automatically.
import { Controller } from "@hotwired/stimulus";

const MESSAGE_SELECTOR = "[data-updated-at]:not([data-tool-message])";
const ANCHOR_SELECTOR = "[data-massimo-anchor]";
const ANCHOR_OFFSET = 24; // px below the viewport top that marks the "current" turn

export default class extends Controller {
  #io = null;
  #mo = null;
  #intersecting = new Map(); // row element -> isIntersecting
  #onScroll = null;
  #ticking = false;
  #currentAnchorId = null;
  // Cached row/anchor lists + derived data, rebuilt only on DOM change (#syncRows) so
  // scroll frames don't re-query the whole viewport.
  #rows = [];
  #anchors = [];
  #messageIds = [];
  #turns = [];

  connect() {
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#intersecting.set(entry.target, entry.isIntersecting);
        this.#publish();
      },
      { root: this.element, threshold: 0 }
    );

    this.#mo = new MutationObserver(() => this.#syncRows());
    this.#mo.observe(this.element, { childList: true });

    this.#onScroll = () => {
      if (this.#ticking) return;
      this.#ticking = true;
      requestAnimationFrame(() => {
        this.#ticking = false;
        this.#publish();
      });
    };
    this.element.addEventListener("scroll", this.#onScroll, { passive: true });

    this.#syncRows();
    // Re-publish after the current task so consumers that connect after us (the outline
    // is rendered later in the DOM) still receive the initial state.
    requestAnimationFrame(() => this.#publish());
  }

  disconnect() {
    this.#io?.disconnect();
    this.#mo?.disconnect();
    this.element.removeEventListener("scroll", this.#onScroll);
    this.#intersecting.clear();
  }

  // Rebuilds the cached row/anchor lists and (un)observes rows as Turbo Streams
  // append/replace messages. The per-change DOM work lives here, not in #publish.
  #syncRows() {
    this.#rows = Array.from(this.element.querySelectorAll(MESSAGE_SELECTOR));
    this.#anchors = Array.from(this.element.querySelectorAll(ANCHOR_SELECTOR));
    this.#messageIds = this.#rows.map((row) => row.id);
    this.#turns = this.#anchors.map((anchor) => ({
      id: anchor.id,
      label: anchor.getAttribute("data-massimo-anchor") || "",
    }));

    const rowSet = new Set(this.#rows);
    rowSet.forEach((row) => {
      if (!this.#intersecting.has(row)) this.#io.observe(row);
    });
    for (const row of [...this.#intersecting.keys()]) {
      if (!rowSet.has(row)) {
        this.#io.unobserve(row);
        this.#intersecting.delete(row);
      }
    }

    this.#publish();
  }

  // The current turn: the last anchor whose top has scrolled to/past a detection line near
  // the viewport top. Stops at the first anchor below the line.
  #computeAnchorId() {
    if (this.#anchors.length === 0) return this.#currentAnchorId;
    const line = this.element.getBoundingClientRect().top + ANCHOR_OFFSET;
    let current = this.#anchors[0].id;
    for (const anchor of this.#anchors) {
      if (anchor.getBoundingClientRect().top <= line) current = anchor.id;
      else break;
    }
    return current;
  }

  #publish() {
    this.#currentAnchorId = this.#computeAnchorId();
    this.dispatch("change", {
      prefix: "massimo-visibility",
      target: window,
      detail: {
        messageIds: this.#messageIds,
        visibleMessageIds: this.#rows
          .filter((row) => this.#intersecting.get(row))
          .map((row) => row.id),
        currentAnchorId: this.#currentAnchorId,
        turns: this.#turns,
      },
    });
  }
}
